import { msg } from "../locales/translate";
import { GD } from "../GD";
import { createFingerprint, getFingerprintPayload, resetFingerprintCache } from "./fingerprint";
import { recordAuthToken, recordReplacedToken, TAuthTokenSource } from "./sessionDebug";
import { ProjectRole } from "../permissions";

const MIN_LOGIN_LENGTH = "usr".length;
const MIN_PASSWORD_LENGTH = "secret".length;

export interface IAuthForm {
	login: string;
	password: string;
	controls: {
		remember: boolean;
	};
}

export interface IAuthVerifyTwoFactorRequest {
	code: string;
}

export type TAuthPendingVerificationType = "twofa" | "device";

export interface IAuth2FAConfirmRequest {
	pending_token: string;
	code: string;
}

export interface IAuthChangePasswordRequest {
	password: string;
	token?: string;
}

export enum AUTH_STATE {
	AUTHORIZED = "authorized",
	UNAUTHORIZED = "unauthorized",
	SELECT_PROJECT = "select_project",
}

export type TUser = {
	id: number;
	email: string;
	name: string;
	password: string;
	signin_attempts: number | null;
	phone: string | null;
	created_at: string;
	updated_at: string | null;
	deleted_at: string | null;
	status: string;
	type: string;
	status_email: string;
	status_2fa: string;
	branch: string;
	projects: { id: number; name: string; roles: ProjectRole[] }[];
};

export type TTwoFactorStatus = "activated" | "disabled";

const isTwoFactorStatus = (value: unknown): value is TTwoFactorStatus => value === "activated" || value === "disabled";

export const resolveTwoFactorStatus = (payload: unknown, fallback: TTwoFactorStatus | null = null): TTwoFactorStatus | null => {
	if (isTwoFactorStatus(payload)) {
		return payload;
	}

	if (!payload || typeof payload !== "object") {
		return fallback;
	}

	const record = payload as Record<string, unknown>;

	if (isTwoFactorStatus(record.status_2fa)) {
		return record.status_2fa;
	}

	for (const key of ["profile", "user"]) {
		const nestedStatus = resolveTwoFactorStatus(record[key]);
		if (nestedStatus) {
			return nestedStatus;
		}
	}

	return fallback;
};

export type TAuthPendingTwoFactor = {
	pending_token: string;
	remember: boolean;
	type: TAuthPendingVerificationType;
};

export interface IAuthAuthorizedResponse {
	profile: TUser;
	token: string;
	require_password_change?: boolean;
}

export interface IAuthTwoFactorRequiredResponse {
	twofa_required: true;
	pending_token: string;
}

export interface IAuthDeviceVerifyRequiredResponse {
	device_verify_required: true;
	pending_token: string;
}

export interface IAuthSession {
	os: string;
	device: string;
	browser: string;
	ip: string;
	created_at: string;
	expires_at: string;
	status: string;
	current: boolean;
}

export interface IAuth2FASetupEmailResponse {
	method: "email";
	project: unknown;
}

export interface IAuth2FASetupTotpResponse {
	url: string;
	pending_token: string;
}

export type TAuthLoginResult = IError | IAuthTwoFactorRequiredResponse | IAuthDeviceVerifyRequiredResponse | null;

type TRegisterClient = {
	company_name: string;
	country: string;
	registration_number: string;
	email: string;
	password: string;
	confirm_password: string;
	email_verification_code: string;
	full_name: string;
};

export class AuthService {
	authState = AUTH_STATE.UNAUTHORIZED;
	profile: TUser | null = null;
	key: string = "";
	forcePasswordChange = false;
	passwordChangeToken = "";
	pendingTwoFactor: TAuthPendingTwoFactor | null = null;
	rememberAuthorizedSession = false;

	constructor() {
		GD.REQ_AUTH_LOGIN.listener = async req => this.handleAuthLogin(req);
		GD.REQ_AUTH_VERIFY_2FA.listener = async req => this.handleVerifyTwoFactor(req);
		GD.REQ_AUTH_2FA_PENDING.listener = async () => this.pendingTwoFactor;
		GD.REQ_AUTH_FINGERPRINT.listener = async () => this.getFingerprint();
		GD.REQ_AUTH_SESSIONS.listener = async () => this.getAuthSessions();
		GD.REQ_AUTH_LOGOUT_OTHERS.listener = async () => this.logoutOtherSessions();
		GD.REQ_AUTH_2FA_SETUP_EMAIL.listener = async () => this.setupTwoFactorEmail();
		GD.REQ_AUTH_2FA_SETUP_TOTP.listener = async () => this.setupTwoFactorTotp();
		GD.REQ_AUTH_2FA_CONFIRM_TOTP.listener = async req => this.confirmTwoFactorTotp(req);
		GD.REQ_AUTH_2FA_DISABLE.listener = async () => this.disableTwoFactor();
		GD.S_AUTH_TOKEN_REPLACED.subscribe(token => this.replaceAuthToken(token));
		GD.S_AUTH_STATUS.onSubscribe = cb => cb!(this.authState);
		GD.S_AUTH_FORCE_PASSWORD_CHANGE.onSubscribe = cb => cb!(this.forcePasswordChange);
		GD.S_PROFILE_PATCH.subscribe(patch => this.patchProfile(patch));

		GD.S_AUTH_LOG_OUT.subscribe(() => {
			localStorage.removeItem("auth");
			this.key = "";
			this.profile = null;
			this.authState = AUTH_STATE.UNAUTHORIZED;
			this.forcePasswordChange = false;
			this.passwordChangeToken = "";
			this.pendingTwoFactor = null;
			this.rememberAuthorizedSession = false;
			resetFingerprintCache();
			GD.S_AUTH_FORCE_PASSWORD_CHANGE.invoke(this.forcePasswordChange);
			GD.S_AUTH_STATUS.invoke(this.authState);
		});

		GD.S_PROJECT_SELECTED.subscribe(p => {
			if (p !== null && this.authState !== AUTH_STATE.AUTHORIZED) {
				this.authState = AUTH_STATE.AUTHORIZED;
				GD.S_AUTH_STATUS.invoke(this.authState);
			}
		});

		GD.REQ_PROFILE.listener = async () => this.profile;

		GD.REQ_AUTH_TOKEN.listener = async () => this.key;

		GD.S_SELECT_PROJECT.subscribe(() => {
			this.authState = AUTH_STATE.SELECT_PROJECT;
			GD.S_AUTH_STATUS.invoke(this.authState);
		});

		GD.REQ_AUTH_CHANGE_PASSWORD.listener = async req => this.changePassword(req);

		GD.REQ_AUTH_REGISTER.listener = async form => {
			if (!form) {
				console.error("NO FORM!");
			}

			//todo: check pass/confirm pass

			const data: TRegisterClient = {
				company_name: form.step1.company_name.trim(),
				country: form.step1.country.trim(),
				registration_number: form.step1.registration_number.trim(),
				email: form.step2.email.trim(),
				password: form.step2.password,
				confirm_password: form.step2.confirm_password,
				email_verification_code: form.step3.email_verification_code.trim(),
				full_name: form.step2.full_name.trim(),
			};

			const response = await GD.REQ_API.request({
				method: "clients/register",
				data: data,
			});

			return response;
		};

		const remember = localStorage.getItem("auth");

		if (remember) {
			try {
				const response = JSON.parse(remember);

				if (response && response.data && response.data.profile && response.data.token) {
					//todo - validate with TUSER
					this.rememberAuthorizedSession = true;
					this.authorize(response.data as IAuthAuthorizedResponse, "stored");
				}
			} catch {
				console.error("cant parse stored auth data");
			}
		}
	}

	patchProfile(patch: Partial<TUser>) {
		if (!this.profile) return;

		this.profile = {
			...this.profile,
			...patch,
		};

		GD.REQ_PROFILE.request();
	}

	persistAuthorizedResponse(response: IAuthAuthorizedResponse, remember: boolean) {
		this.rememberAuthorizedSession = remember;

		if (remember) {
			localStorage.setItem("auth", JSON.stringify({ data: response }));
			return;
		}

		localStorage.removeItem("auth");
	}

	async handleAuthLogin(form: IAuthForm): Promise<TAuthLoginResult> {
		if (!form) return null;

		if (form.login.length < MIN_LOGIN_LENGTH || form.password.length < MIN_PASSWORD_LENGTH) {
			return { code: 1, message: msg("checkLoginAndPassword") };
		}

		this.passwordChangeToken = "";
		this.pendingTwoFactor = null;

		const fingerprintPayload = await getFingerprintPayload();

		const response = (await GD.REQ_API.request({
			method: "auth/login",
			excludeAuth: true,
			data: {
				login: form.login,
				password: form.password,
				fingerprint: fingerprintPayload.fingerprint,
				fingerprint_data: fingerprintPayload.fingerprint_data,
			},
		})) as IAPIResponse<IAuthAuthorizedResponse | IAuthTwoFactorRequiredResponse | IAuthDeviceVerifyRequiredResponse>;

		if (response.error) {
			if (response.error.message === "PASSWORD_CHANGE_REQUIRED" && response.error.temp_token) {
				this.passwordChangeToken = response.error.temp_token;
			}

			return response.error;
		}

		const authResponse = response.data;

		if (!authResponse) {
			return { code: -1, message: msg("emptyResponse") };
		}

		if ("twofa_required" in authResponse && authResponse.twofa_required) {
			if (!authResponse.pending_token) {
				return { code: -1, message: msg("pending2faTokenIsMissing") };
			}

			this.pendingTwoFactor = {
				pending_token: authResponse.pending_token,
				remember: form.controls.remember,
				type: "twofa",
			};

			return authResponse;
		}

		if ("device_verify_required" in authResponse && authResponse.device_verify_required) {
			if (!authResponse.pending_token) {
				return { code: -1, message: msg("pendingDeviceVerificationTokenIsMissing") };
			}

			this.pendingTwoFactor = {
				pending_token: authResponse.pending_token,
				remember: form.controls.remember,
				type: "device",
			};

			return authResponse;
		}

		const authorizedResponse = authResponse as IAuthAuthorizedResponse;

		this.persistAuthorizedResponse(authorizedResponse, form.controls.remember);
		this.authorize(authorizedResponse, "login");

		return null;
	}

	async handleVerifyTwoFactor(req: IAuthVerifyTwoFactorRequest): Promise<IError | null> {
		if (!req?.code?.trim()) {
			return { code: -1, message: msg("verificationCodeIsRequired") };
		}

		if (!this.pendingTwoFactor?.pending_token) {
			return { code: -1, message: msg("text2faSessionExpiredPleaseSignInAgain") };
		}

		const verifyMethod = this.pendingTwoFactor.type === "device" ? "auth/device/verify" : "auth/2fa/verify";

		const response = (await GD.REQ_API.request({
			method: verifyMethod,
			excludeAuth: true,
			data: {
				pending_token: this.pendingTwoFactor.pending_token,
				code: req.code.trim(),
			},
		})) as IAPIResponse<IAuthAuthorizedResponse>;

		if (response.error) {
			return response.error;
		}

		if (!response.data) {
			return { code: -1, message: msg("emptyResponse") };
		}

		const verifySource: TAuthTokenSource = this.pendingTwoFactor.type === "device" ? "device" : "2fa";
		this.persistAuthorizedResponse(response.data, this.pendingTwoFactor.remember);
		this.pendingTwoFactor = null;
		this.authorize(response.data, verifySource);

		return null;
	}

	async getFingerprint(): Promise<string | null> {
		try {
			return await createFingerprint();
		} catch (error) {
			console.error("Failed to generate fingerprint", error);
			return null;
		}
	}

	replaceAuthToken(token: string) {
		this.key = token;
		recordReplacedToken(token);

		if (!this.profile || !this.rememberAuthorizedSession) {
			return;
		}

		localStorage.setItem(
			"auth",
			JSON.stringify({
				data: {
					profile: this.profile,
					token: this.key,
					require_password_change: this.forcePasswordChange,
				},
			}),
		);
	}

	async authorize(response: IAuthAuthorizedResponse, source: TAuthTokenSource = "login") {
		this.profile = response.profile;
		this.key = response.token;
		recordAuthToken(response.token, source);
		this.passwordChangeToken = "";
		this.pendingTwoFactor = null;
		this.forcePasswordChange = Boolean(response.require_password_change);
		GD.S_AUTH_FORCE_PASSWORD_CHANGE.invoke(this.forcePasswordChange);

		if (this.profile.projects.length === 1) {
			GD.S_PROJECT_SELECTED.invoke(this.profile.projects[0]);
		} else {
			GD.S_SELECT_PROJECT.invoke();
		}
	}

	async changePassword(req: IAuthChangePasswordRequest): Promise<IAPIResponse> {
		if (!req || !req.password) return { error: { code: -1, message: msg("passwordIsRequired") } };

		const token = req.token ?? this.passwordChangeToken ?? this.key;

		if (!token) {
			return {
				error: { code: -1, message: msg("passwordChangeTokenIsRequired") },
			};
		}

		const res: IAPIResponse = await GD.REQ_API.request({
			method: "auth/password/change",
			excludeAuth: true,

			data: {
				password: req.password,
				temp_token: token,
			},
		});

		if (!res.error) {
			this.passwordChangeToken = "";
			this.forcePasswordChange = false;
			GD.S_AUTH_FORCE_PASSWORD_CHANGE.invoke(this.forcePasswordChange);
		}

		return res;
	}

	async setupTwoFactorEmail(): Promise<IAPIResponse<IAuth2FASetupEmailResponse>> {
		const response = (await GD.REQ_API.request({
			method: "auth/2fa/setup/email",
		})) as IAPIResponse<IAuth2FASetupEmailResponse>;

		if (!response.error) {
			const status_2fa = resolveTwoFactorStatus(response.data, "activated");

			if (status_2fa) {
				this.patchProfile({ status_2fa });
			}
		}

		return response;
	}

	async setupTwoFactorTotp(): Promise<IAPIResponse<IAuth2FASetupTotpResponse>> {
		return (await GD.REQ_API.request({
			method: "auth/2fa/setup/totp",
		})) as IAPIResponse<IAuth2FASetupTotpResponse>;
	}

	async confirmTwoFactorTotp(req: IAuth2FAConfirmRequest): Promise<IAPIResponse<boolean>> {
		if (!req?.pending_token || !req.code?.trim()) {
			return {
				error: { code: -1, message: msg("pendingTokenAndVerificationCodeAreRequired") },
			};
		}

		const response = (await GD.REQ_API.request({
			method: "auth/2fa/setup/totp/confirm",
			excludeAuth: true,
			data: {
				pending_token: req.pending_token,
				code: req.code.trim(),
			},
		})) as IAPIResponse<boolean>;

		if (!response.error) {
			const status_2fa = resolveTwoFactorStatus(response.data, "activated");

			if (status_2fa) {
				this.patchProfile({ status_2fa });
			}
		}

		return response;
	}

	async disableTwoFactor(): Promise<IAPIResponse<boolean>> {
		const response = (await GD.REQ_API.request({
			method: "auth/2fa/disable",
		})) as IAPIResponse<boolean>;

		if (!response.error) {
			const status_2fa = resolveTwoFactorStatus(response.data, "disabled");

			if (status_2fa) {
				this.patchProfile({ status_2fa });
			}
		}

		return response;
	}

	async getAuthSessions(): Promise<IAPIResponse<IAuthSession[]>> {
		return (await GD.REQ_API.request({
			method: "users/auth/sessions",
			data: {},
		})) as IAPIResponse<IAuthSession[]>;
	}

	async logoutOtherSessions(): Promise<IAPIResponse<boolean>> {
		return (await GD.REQ_API.request({
			method: "auth/logout/others",
			data: {},
		})) as IAPIResponse<boolean>;
	}
}

/*

const p = await GD.REQ_CURRENT_PROJECT.request();
this.authState = !p ? AUTH_STATE.SELECT_PROJECT : AUTH_STATE.AUTHORIZED;
// change status

GD.S_AUTH_STATUS.invoke(this.authState)
}*/
